
























































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import HeatmapTable, {
  HeatmapTableColumn,
  HeatmapTableRow,
} from "../HeatmapTable.vue";
import EntryItem from "@/components/EntryItem.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import { SlotObject } from "@/components/TimeSlotPicker.vue";
import {
  ComparisonGroup,
  Company,
  Brand,
  Product,
  User,
  Media,
} from "@/models";
import InvestorsMixins from "@/mixins/http/Investor";
import Entry from "@/models/Entry";
import number from "@/filters/Number";
import beautify from "@/filters/Beautify";
import { Getter } from "vuex-class";
import { FEATURE_BREAKS } from "@/constants";
import ConfrontationBenchmarkHelper from "@/services/ConfrontationBenchmarkHelper";
import {
  BenchmarkGranularity,
  BenchmarkTaxonomy,
} from "@/models/interfaces/Benchmark";

export interface ComparatorTableFilter {
  group: ComparisonGroup | null;
  timeSlots: SlotObject[];
  date: {
    from: string | null;
    to: string | null;
  };
  media: {
    radio: boolean;
    tv: boolean;
  };
  grpTarget?: string;
  benchmarkTaxonomy: BenchmarkTaxonomy;
  benchmarkGranularity?: BenchmarkGranularity;
}

const requestTag = "comparisonKPI";

@Component({
  components: {
    HeatmapTable,
    VSwitchButton,
    MultiLevelSelect,
    EntryItem,
  },
})
export default class ComparatorTable extends Mixins(InvestorsMixins) {
  @Getter("user") readonly user!: User;
  @Prop() readonly filter!: ComparatorTableFilter;
  @Prop({
    default: () => {
      return [];
    },
  })
  readonly hiddenItems?: Entry[];
  @Prop({ default: false }) readonly hasBenchmark!: boolean;

  @Watch("items", { immediate: true })
  @Watch("hasBenchmark", { immediate: true })
  onItemsChange() {
    this.load();
  }

  @Watch("selected")
  @Watch("heatmap")
  onSettingsChange() {
    this.saveSettings();
  }

  @Watch("filter", { deep: true })
  onFilterChange() {
    this.load();
    this.initializeOptions();
  }

  heatmap = false;
  loading = false;

  get hasBreaks() {
    return this.user.hasFeature(FEATURE_BREAKS);
  }

  get columns(): HeatmapTableColumn[] {
    const alwaysOnColumns: HeatmapTableColumn[] = [
      {
        name: "weekdays",
        label: "Spot Weekdays %",
        tooltip: "% degli spot trasmessi nei giorni della settimana (lun-ven)",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      {
        name: "weekend",
        label: "Spot Weekend %",
        tooltip: "% degli spot trasmessi nel week-end (sab-dom)",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      {
        name: "driveTime",
        label: "Drive Time %",
        tooltip:
          "% degli spot Radio trasmessi nelle fasce 06:00-09:00 e 17:00-20:00",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotRadio();
        },
      },
      {
        name: "morningTime",
        label: "Morning Time %",
        tooltip: "% degli spot TV trasmessi nelle fascia 06:00-12:00",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotTV();
        },
      },
      {
        name: "dayTime",
        label: "Day Time %",
        tooltip: "% degli spot TV trasmessi nelle fascia 12:00-18:30",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotTV();
        },
      },
      {
        name: "accessPrimeTime",
        label: "Access Prime Time %",
        tooltip: "% degli spot TV trasmessi nelle fascia 18:30-20:00",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotTV();
        },
      },
      {
        name: "primeTime",
        label: "Prime Time %",
        tooltip: "% degli spot TV trasmessi nelle fascia 20:00-01:00",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotTV();
        },
      },
      {
        name: "nightTime",
        label: "Night Time %",
        tooltip: "% degli spot TV trasmessi nelle fascia 01:00-06:00",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark || this.isNotTV();
        },
      },
    ];

    let grpColumns: HeatmapTableColumn[] = [];

    if (this.filter.grpTarget) {
      grpColumns = [
        {
          name: "grp",
          label: "GRP",
          tooltip: "GRP Auditel rilevato",
          heatmapDisabled: (row: any) => {
            return row.isBenchmark;
          },
        },
        {
          name: "grp_over_spots",
          label: "GRP / Spots",
          tooltip: "GRP Auditel rilevato diviso numero di spot",
          heatmapDisabled: (row: any) => {
            return row.isBenchmark;
          },
        },
        {
          name: "audience",
          label: "Contatti",
          tooltip: "Numero di contatti Auditel",
          heatmapDisabled: (row: any) => {
            return row.isBenchmark;
          },
        },
      ];
    }

    let breaksColumns: HeatmapTableColumn[] = [];

    breaksColumns = [
      {
        name: "position",
        label: "Posizione Singola %",
        tooltip:
          "%, sul totale degli spot pianificati, di quelli trasmessi in posizione singola",
        locked: !this.hasBreaks,
        disabled: !this.hasBreaks,
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      {
        name: "psu",
        label: "PSU %",
        tooltip:
          "%, sul totale degli spot pianificati, di quelli trasmessi in Prima, Seconda o Ultima posizione",
        locked: !this.hasBreaks,
        disabled: !this.hasBreaks,
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      {
        name: "averagePosition",
        label: "Posizionamento Medio",
        tooltip: "Posizione media degli spot / lunghezza media di un break",
        heatmapDisabled: true,
        locked: !this.hasBreaks,
        disabled: !this.hasBreaks,
      },
      {
        name: "breakCompetitor",
        label: "Break con Competitor %",
        tooltip:
          "% dei break all'interno dei quali sono presenti spot di Aziende/Brand/Prodotti appartenenti alla stessa Classe di prodotto",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
        locked: !this.hasBreaks,
        disabled: !this.hasBreaks,
      },
    ];

    return [
      {
        name: "entry",
        label: "Azienda",
        notSortable: true,
        heading: true,
      },
      {
        name: "spot",
        label: "Spot",
        tooltip: "Quantità di spot pianificati",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      {
        name: "seconds",
        label: "Secondi",
        tooltip: "Quantità di secondi pianificati",
        heatmapDisabled: (row: any) => {
          return row.isBenchmark;
        },
      },
      ...grpColumns,
      ...alwaysOnColumns,
      ...breaksColumns,
    ];
  }

  rows: HeatmapTableRow[] = [];

  selected: string[] = [];

  options: MultiLevelObject[] = [];

  get selectedColumns(): HeatmapTableColumn[] {
    return this.columns.filter((column) => {
      return this.selected.indexOf(column.name) >= 0 || column.name == "entry";
    });
  }

  get selectedMultipleValue(): Array<string[]> {
    const selected: Array<string[]> = [];
    selected[0] = this.selected;

    return selected;
  }

  get visibleRows(): HeatmapTableRow[] {
    return this.rows.filter((row: HeatmapTableRow) => {
      const hiddenItem = this.hiddenItems?.find((item: Entry) => {
        return (
          item.id == row.entry.id && item.constructor == row.entry.constructor
        );
      });

      return hiddenItem == undefined;
    });
  }

  get hasAuditel(): boolean {
    return isFinite(parseInt(this.filter?.grpTarget ?? ""));
  }

  isNotTV(): boolean {
    return this.filter.media.tv == false;
  }

  isNotRadio(): boolean {
    return this.filter.media.radio == false;
  }

  onEntryClick(item: Company | Brand | Product) {
    this.$router.push({
      name: "InvestorSummary",
      params: {
        type: item.type,
        id: item.id,
      },
      query: {
        from: this.filter.date.from,
        to: this.filter.date.to,
        radio: this.filter.media.radio ? "1" : "0",
        tv: this.filter.media.tv ? "1" : "0",
      },
    });
  }

  created() {
    this.loadSettings();
    this.initializeOptions();
  }

  protected async load() {
    if (this.filter.group == null) {
      return;
    }

    this.loading = true;
    const [items, benchmarkItems] = await Promise.all([
      this.getComparisonsKPIs(
        this.filter.group.id,
        this.getPostData(),
        requestTag
      ),
      this.getBenchmarkKpisItems(),
    ]);

    const benchmarkRows = this.hasBenchmark
      ? this.parseItems(benchmarkItems).map((benchmarkRow) => ({
          ...benchmarkRow,
          isBenchmark: true,
          fixedAt: "bottom",
        }))
      : [];
    const itemsRows = this.parseItems(items);
    this.rows = [...itemsRows, ...benchmarkRows];
    this.loading = false;
  }

  private getBenchmarkKpisItems() {
    if (!this.hasBenchmark) {
      return [];
    }

    const data = {
      filter: {
        time_slots: this.filter.timeSlots,
        date_range: [this.filter.date.from, this.filter.date.to],
        sector: this.filter.benchmarkTaxonomy?.sector
          ? Array.isArray(this.filter.benchmarkTaxonomy?.sector)
            ? this.filter.benchmarkTaxonomy?.sector
            : [this.filter.benchmarkTaxonomy.sector]
          : [],
        category: this.filter.benchmarkTaxonomy?.category
          ? Array.isArray(this.filter.benchmarkTaxonomy?.category)
            ? this.filter.benchmarkTaxonomy?.category
            : [this.filter.benchmarkTaxonomy.category]
          : [],
        class: this.filter.benchmarkTaxonomy?.class
          ? Array.isArray(this.filter.benchmarkTaxonomy?.class)
            ? this.filter.benchmarkTaxonomy?.class
            : [this.filter.benchmarkTaxonomy.class]
          : [],
      },
      benchmark_granularity: this.filter.benchmarkGranularity,
      ...(this.filter.grpTarget ? { grpTarget: this.filter.grpTarget } : {}),
    };

    return this.getComparisonsBenchmarkKPIs(
      this.filter.group!.id,
      data,
      requestTag
    );
  }

  // eslint-disable-next-line
  protected parseItems(items: any[]) {
    return items.map((item) => {
      const totals = {
        spot: 0,
        seconds: 0,
        weekends: 0,
        tv: 0,
        radio: 0,
        grp: 0.0,
        audience: 0,
        breaks: 0,
        position: 0,
        psu: 0,
        breakCompetitor: 0,
        averageBreakPosition: 0,
        averageBreakLength: 0,
      };

      const kpis = item.kpis;

      if (this.filter.media.radio) {
        totals.spot += kpis.radioDetectionsCount;
        totals.seconds += kpis.radioSecondsCount;
        totals.weekends += kpis.radioWeekendsCount;
        totals.breaks += kpis.radioBreaks ?? 0;
        totals.position += kpis.radioPosition;
        totals.psu += kpis.radioPsu ?? 0;
        totals.breakCompetitor += kpis.radioBreakCompetitor ?? 0;
        totals.averageBreakPosition = !isNaN(
          parseFloat(kpis.radioAverageBreakPosition)
        )
          ? parseFloat(kpis.radioAverageBreakPosition)
          : 0;
        totals.averageBreakLength = !isNaN(
          parseFloat(kpis.radioAverageBreakLength)
        )
          ? parseFloat(kpis.radioAverageBreakLength)
          : 0;
      }

      if (this.filter.media.tv) {
        totals.spot += kpis.tvDetectionsCount;
        totals.seconds += kpis.tvSecondsCount;
        totals.weekends += kpis.tvWeekendsCount;
        totals.grp += parseFloat(kpis.grpCount) ?? 0;
        totals.audience += kpis.audienceCount ?? 0;
        totals.breaks += kpis.tvBreaks ?? 0;
        totals.position += kpis.tvPosition;
        totals.psu += kpis.tvPsu ?? 0;
        totals.breakCompetitor += kpis.tvBreakCompetitor ?? 0;
        totals.averageBreakPosition = !isNaN(
          parseFloat(kpis.tvAverageBreakPosition)
        )
          ? parseFloat(kpis.tvAverageBreakPosition)
          : 0;
        totals.averageBreakLength = !isNaN(
          parseFloat(kpis.tvAverageBreakLength)
        )
          ? parseFloat(kpis.tvAverageBreakLength)
          : 0;
      }

      if (this.filter.media.tv && this.filter.media.radio) {
        const tvPos = !isNaN(parseFloat(kpis.tvAverageBreakPosition))
          ? parseFloat(kpis.tvAverageBreakPosition)
          : 0;
        const radioPos = !isNaN(parseFloat(kpis.radioAverageBreakPosition))
          ? parseFloat(kpis.radioAverageBreakPosition)
          : 0;
        totals.averageBreakPosition =
          (tvPos * kpis.tvBreaks + radioPos * kpis.radioBreaks) /
          (kpis.tvBreaks + kpis.radioBreaks);

        const tvLen = !isNaN(parseFloat(kpis.tvAverageBreakLength))
          ? parseFloat(kpis.tvAverageBreakLength)
          : 0;
        const radioLen = !isNaN(parseFloat(kpis.radioAverageBreakLength))
          ? parseFloat(kpis.radioAverageBreakLength)
          : 0;

        totals.averageBreakLength =
          (tvLen * kpis.tvBreaks + radioLen * kpis.radioBreaks) /
          (kpis.tvBreaks + kpis.radioBreaks);
      }

      totals.tv += kpis.tvDetectionsCount;
      totals.radio += kpis.radioDetectionsCount;

      if (this.filter.media.radio) {
        totals.grp = NaN;
        totals.audience = NaN;
      }

      totals.averageBreakPosition = isNaN(totals.averageBreakPosition)
        ? 0
        : Math.round(totals.averageBreakPosition);
      totals.averageBreakLength = isNaN(totals.averageBreakLength)
        ? 0
        : Math.round(totals.averageBreakLength);

      return {
        entry: item,
        spot: totals.spot,
        seconds: totals.seconds,
        weekdays: totals.spot
          ? 100 - this.getPercentage(totals.spot, totals.weekends)
          : 0,
        weekend: this.getPercentage(totals.spot, totals.weekends),
        driveTime: this.getPercentage(totals.radio, kpis.driveTime),
        morningTime: this.getPercentage(totals.tv, kpis.morningTime),
        dayTime: this.getPercentage(totals.tv, kpis.dayTime),
        accessPrimeTime: this.getPercentage(totals.tv, kpis.accessPrimeTime),
        primeTime: this.getPercentage(totals.tv, kpis.primeTime),
        nightTime: this.getPercentage(totals.tv, kpis.nightTime),
        grp: isNaN(totals.grp) ? "N/A" : totals.grp,
        audience: isNaN(totals.audience) ? "N/A" : totals.audience,
        grp_over_spots: isNaN(totals.grp)
          ? "N/A"
          : totals.spot == 0
          ? 0
          : totals.grp / totals.spot,
        position: totals.position
          ? this.getPercentage(totals.spot, totals.position)
          : "-",
        psu:
          totals.psu != 0 ? this.getPercentage(totals.spot, totals.psu) : "-",
        averagePosition:
          beautify(totals.averageBreakPosition, "-", "-") +
          "/" +
          beautify(totals.averageBreakLength, "-", "-"),
        breakCompetitor: totals.breakCompetitor
          ? this.getPercentage(totals.breaks, totals.breakCompetitor)
          : "-",
      };
    });
  }

  protected initializeOptions() {
    this.options = this.columns
      .filter((column) => column.name != "entry")
      .map((column) => {
        return {
          id: column.name,
          text: column.label,
          selected: true,
          locked: column.locked,
        };
      });

    if (this.selected.length) {
      this.options = this.options.map((option) => {
        option.selected = this.selected.indexOf(option.id) >= 0;
        return option;
      });
    } else {
      this.selected = this.options.map((option) => {
        return option.id;
      });
    }
  }

  protected loadSettings() {
    const data = localStorage.getItem(`${this.$options.name}-settings`);

    if (data) {
      const settings = JSON.parse(data);

      if (settings.heatmap != undefined) {
        this.heatmap = settings.heatmap;
      }

      if (settings.selected != undefined) {
        this.selected = this.removeLockedColumns(settings.selected);
      }
    }
  }

  protected saveSettings() {
    const settings = {
      heatmap: this.heatmap,
      selected: this.selected,
    };

    localStorage.setItem(
      `${this.$options.name}-settings`,
      JSON.stringify(settings)
    );
  }

  protected getPostData() {
    let grpTarget = undefined;
    if (this.filter.grpTarget) {
      grpTarget = {
        grpTarget: this.filter.grpTarget,
      };
    }
    return {
      filter: {
        time_slots: this.filter.timeSlots,
        date_range: [this.filter.date.from, this.filter.date.to],
      },
      ...grpTarget,
    };
  }

  protected getPercentage(total: number, value: number) {
    return total ? (value / total) * 100 : 0;
  }

  protected onSelectedMultipleValue(selected: Array<string[]>) {
    if (selected[0] !== undefined) {
      this.selected = selected[0];
    }
  }

  protected removeLockedColumns(selected: string[]): string[] {
    const lockedColumns = this.columns
      .filter((c: HeatmapTableColumn) => c.locked)
      .map((c: HeatmapTableColumn) => c.name);
    return selected.filter((s) => !lockedColumns.includes(s));
  }
}
